import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { AssignmentDueGroups, sortAssignmentsByDueDate } from "@/components/AssignmentDueGroups";
import { DashboardSkeleton, LoadError } from "@/components/PageState";

/**
 * Read-only Today view for a parent watching over a student.
 */
export default function ParentStudentView() {
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [bySubject, setBySubject] = useState({});
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [full, subs] = await Promise.all([
          api.get(`/parent/student/${studentId}/full`),
          api.get("/curriculum/subjects"),
        ]);
        setStudent(full.data.student); setSubjects(subs.data);
        setMetrics(full.data.metrics); setBySubject(full.data.by_subject || {}); setError("");
        const open = full.data.open_assignments || full.data.assignments.filter((assignment) =>
          ["assigned", "in_progress", "returned"].includes(assignment.status)
        );
        setAssignments(sortAssignmentsByDueDate(open));
      } catch (requestError) { setError(requestError?.response?.data?.detail || "Student dashboard view could not load."); }
    })();
  }, [studentId, retry]);

  if (error) return <LoadError message={error} onRetry={()=>{setError("");setRetry((value)=>value+1);}} testId="parent-view-load-error" />;
  if (!student || !metrics) return <DashboardSkeleton testId="parent-view-loading" cards={4} />;

  return (
    <div className="space-y-6" data-testid="parent-student-view">
      <Link to={`/parent/student/${studentId}`} data-testid="parent-view-back" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700"><ArrowLeft size={14} /> Back to student</Link>

      <div>
        <div className="chip mb-2">Viewing as parent · read-only</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">{student.name}'s Today</h1>
        <p className="text-slate-600 text-sm">This is what your student sees — you can't complete lessons on their behalf.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="p-4 rounded-2xl bg-white border border-slate-200">
          <div className="text-xs text-slate-500 uppercase tracking-widest">Curriculum lessons completed</div>
          <div data-testid="parent-view-curriculum-lessons" className="font-display text-2xl font-bold text-teal-700">{metrics.curriculum_lessons_completed}/{metrics.curriculum_lessons_total}</div>
        </div>
        <div className="p-4 rounded-2xl bg-white border border-slate-200">
          <div className="text-xs text-slate-500 uppercase tracking-widest">Assignments turned in</div>
          <div data-testid="parent-view-assignments-turned-in" className="font-display text-2xl font-bold text-slate-900">{metrics.assignments_turned_in}</div>
        </div>
        <div className="p-4 rounded-2xl bg-white border border-slate-200">
          <div className="text-xs text-slate-500 uppercase tracking-widest">Assignments graded</div>
          <div data-testid="parent-view-assignments-graded" className="font-display text-2xl font-bold text-slate-900">{metrics.assignments_graded}</div>
        </div>
        <div className="p-4 rounded-2xl bg-white border border-slate-200"><div className="text-xs text-slate-500 uppercase tracking-widest">Points</div><div className="font-display text-2xl font-bold text-slate-900">{student.points ?? 0}</div></div>
      </div>

      <AssignmentDueGroups assignments={assignments} testIdPrefix="parent-open-assignments" />

      <section>
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Subjects</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {subjects.map((s) => {
            const summary = bySubject[s.slug] || { lessons_total: s.lesson_count || 0, lessons_done: 0 };
            const total = summary.lessons_total;
            const done = summary.lessons_done;
            return (
              <div key={s.slug} className="p-5 rounded-2xl bg-white border border-slate-200">
                <div className="font-display font-semibold text-slate-900">{s.title}</div>
                <div className="text-xs text-slate-500">{s.tagline}</div>
                <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-teal-600" style={{ width: `${Math.min(100, (done/total)*100)}%` }} />
                </div>
                <div className="mt-2 text-xs text-slate-500">{done}/{total} completed</div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
